'use client';

/**
 * 뮤직비디오 액션 훅
 *
 * 뮤직비디오 생성, 삭제, 씬 생성, 클립 생성, 합성 등
 * 모든 뮤직비디오 관련 mutation을 하나의 훅으로 통합합니다.
 */

import { useQueryClient } from '@tanstack/react-query';
import {
  invalidateMusicVideoList,
  invalidateMusicVideo,
  invalidateMusicVideoAll,
} from '@/lib/api/cacheUtils';
import {
  useMusicVideoCreate,
  useMusicVideoDelete,
  useMusicVideoGenerateScenes,
  useMusicVideoGenerateClips,
  useMusicVideoGenerateSceneClip,
  useMusicVideoUpdateScene,
  useMusicVideoSelectClip,
  useMusicVideoCompose,
} from '@/lib/api/orval';
import type { MusicVideoCreateRequest, PatchedMusicVideoSceneUpdateRequest } from '@/lib/api/orval/models';
import { createMutationErrorHandler } from '@/lib/utils/errorUtils';
import type { SceneUpdateFormData } from '@/lib/validations';
import { useToast } from './useToast';

export interface UseMusicVideoActionsOptions {
  musicVideoId?: string;
  onCreateSuccess?: (id: string) => void;
  onDeleteSuccess?: () => void;
}

export function useMusicVideoActions({
  musicVideoId,
  onCreateSuccess,
  onDeleteSuccess,
}: UseMusicVideoActionsOptions = {}) {
  const queryClient = useQueryClient();
  const { success: toastSuccess, error: toastError } = useToast();

  // 뮤직비디오 생성
  const createMutation = useMusicVideoCreate({
    mutation: {
      onSuccess: (data) => {
        invalidateMusicVideoList(queryClient);
        toastSuccess('뮤직비디오가 생성되었습니다.');
        onCreateSuccess?.(data.id);
      },
      onError: createMutationErrorHandler(toastError, '뮤직비디오 생성 실패'),
    },
  });

  // 뮤직비디오 삭제
  const deleteMutation = useMusicVideoDelete({
    mutation: {
      onSuccess: () => {
        invalidateMusicVideoList(queryClient);
        toastSuccess('뮤직비디오가 삭제되었습니다.');
        onDeleteSuccess?.();
      },
      onError: createMutationErrorHandler(toastError, '뮤직비디오 삭제 실패'),
    },
  });

  // 씬 생성 (Claude AI)
  const generateScenesMutation = useMusicVideoGenerateScenes({
    mutation: {
      onSuccess: () => {
        if (musicVideoId) { invalidateMusicVideo(queryClient, musicVideoId); }
        toastSuccess('씬 생성이 시작되었습니다. 잠시 기다려주세요.');
      },
      onError: createMutationErrorHandler(toastError, '씬 생성 실패'),
    },
  });

  // 클립 생성 (Kling AI - 전체)
  const generateClipsMutation = useMusicVideoGenerateClips({
    mutation: {
      onSuccess: () => {
        if (musicVideoId) { invalidateMusicVideo(queryClient, musicVideoId); }
        toastSuccess('클립 생성이 시작되었습니다. 몇 분 정도 소요됩니다.');
      },
      onError: createMutationErrorHandler(toastError, '클립 생성 실패'),
    },
  });

  // 특정 씬 클립 재생성
  const regenerateClipMutation = useMusicVideoGenerateSceneClip({
    mutation: {
      onSuccess: () => {
        if (musicVideoId) { invalidateMusicVideo(queryClient, musicVideoId); }
        toastSuccess('클립 재생성이 시작되었습니다.');
      },
      onError: createMutationErrorHandler(toastError, '클립 재생성 실패'),
    },
  });

  // 씬 프롬프트 수정
  const updateSceneMutation = useMusicVideoUpdateScene({
    mutation: {
      onSuccess: () => {
        if (musicVideoId) { invalidateMusicVideo(queryClient, musicVideoId); }
        toastSuccess('씬이 수정되었습니다.');
      },
      onError: createMutationErrorHandler(toastError, '씬 수정 실패'),
    },
  });

  // 클립 선택
  const selectClipMutation = useMusicVideoSelectClip({
    mutation: {
      onSuccess: () => {
        if (musicVideoId) { invalidateMusicVideo(queryClient, musicVideoId); }
        toastSuccess('클립이 선택되었습니다.');
      },
      onError: createMutationErrorHandler(toastError, '클립 선택 실패'),
    },
  });

  // 최종 합성
  const composeMutation = useMusicVideoCompose({
    mutation: {
      onSuccess: () => {
        if (musicVideoId) { invalidateMusicVideoAll(queryClient, musicVideoId); }
        toastSuccess('합성이 시작되었습니다. 잠시 기다려주세요.');
      },
      onError: createMutationErrorHandler(toastError, '합성 실패'),
    },
  });

  // 핸들러 함수들
  const handleCreate = async (data: {
    trackVersion?: string;
    stylePreset?: string;
    mood?: string;
    theme?: string;
    aspectRatio?: string;
    klingMode?: string;
  }) => {
    await createMutation.mutateAsync({ data: data as MusicVideoCreateRequest });
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync({ id });
  };

  const handleGenerateScenes = async (id: string) => {
    await generateScenesMutation.mutateAsync({ id });
  };

  const handleGenerateClips = async (id: string) => {
    await generateClipsMutation.mutateAsync({ id });
  };

  const handleRegenerateClip = async (id: string, sceneId: string) => {
    await regenerateClipMutation.mutateAsync({ id, sceneId });
  };

  const handleUpdateScene = async (
    id: string,
    sceneId: string,
    data: SceneUpdateFormData,
  ) => {
    await updateSceneMutation.mutateAsync({
      id,
      sceneId,
      data: data as unknown as PatchedMusicVideoSceneUpdateRequest,
    });
  };

  const handleSelectClip = async (id: string, sceneId: string, clipId: string) => {
    await selectClipMutation.mutateAsync({ id, sceneId, clipId });
  };

  const handleCompose = async (id: string) => {
    await composeMutation.mutateAsync({ id });
  };

  return {
    // 생성/삭제
    isCreating: createMutation.isPending,
    isDeleting: deleteMutation.isPending,
    handleCreate,
    handleDelete,

    // 씬 관련
    isGeneratingScenes: generateScenesMutation.isPending,
    isUpdatingScene: updateSceneMutation.isPending,
    handleGenerateScenes,
    handleUpdateScene,

    // 클립 관련
    isGeneratingClips: generateClipsMutation.isPending,
    isRegeneratingClip: regenerateClipMutation.isPending,
    isSelectingClip: selectClipMutation.isPending,
    handleGenerateClips,
    handleRegenerateClip,
    handleSelectClip,

    // 합성
    isComposing: composeMutation.isPending,
    handleCompose,
  };
}

export type UseMusicVideoActionsReturn = ReturnType<typeof useMusicVideoActions>;
